import { ArrowUp, ArrowDown } from "@phosphor-icons/react";

/**
 * Bloomberg-style KPI card. Monospace tabular numbers, sharp corners,
 * subtle glow when trend is positive/negative.
 *
 * Props:
 *   label     — small uppercase label ("NET WORTH")
 *   value     — big number (already formatted string)
 *   sublabel  — smaller line under value
 *   trend     — { direction: "up"|"down"|"flat", label: string }
 *   accent    — CSS color used for left border + trend
 *   Icon      — Phosphor icon component
 *   big       — larger variant
 */
export default function KPICard({
  label,
  value,
  sublabel,
  trend,
  accent = "#2563EB",
  Icon,
  big = false,
  testId,
  onClick,
}) {
  const clickable = typeof onClick === "function";
  return (
    <div
      data-testid={testId}
      onClick={onClick}
      className={`relative overflow-hidden rounded-lg border border-white/8 bg-[#0F1017] p-4 md:p-5 transition ${
        clickable ? "cursor-pointer hover:border-white/20 hover:bg-[#141621]" : ""
      }`}
      style={{
        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.03), 0 4px 20px rgba(0,0,0,0.3)",
      }}
    >
      {/* Left accent bar */}
      <div
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: accent, boxShadow: `0 0 12px ${accent}88` }}
      />

      <div className="flex items-start justify-between mb-3">
        <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/40">
          {label}
        </div>
        {Icon && (
          <Icon
            size={16}
            weight="bold"
            className="opacity-40"
            style={{ color: accent }}
          />
        )}
      </div>

      <div
        className={`font-mono font-bold tabular-nums leading-none tracking-tight text-white ${
          big ? "text-3xl md:text-4xl" : "text-xl md:text-2xl"
        }`}
      >
        {value}
      </div>

      {(sublabel || trend) && (
        <div className="mt-3 flex items-center justify-between gap-2 text-[11px] font-mono">
          <span className="text-white/50 truncate">{sublabel}</span>
          {trend && (
            <span
              className={`flex items-center gap-1 shrink-0 font-bold uppercase tracking-wider ${
                trend.direction === "up"
                  ? "text-emerald-400"
                  : trend.direction === "down"
                  ? "text-red-400"
                  : "text-white/40"
              }`}
            >
              {trend.direction === "up" && <ArrowUp size={11} weight="bold" />}
              {trend.direction === "down" && <ArrowDown size={11} weight="bold" />}
              {trend.label}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
